import React from "react";
import { useTranslation } from "react-i18next";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { u3125FormatCurrency, NS } from "../../U3125";
import {
  u3125FormatArAgingCustomerLabel,
  u3125FormatArAgingProjectLabel,
  u3125ArAgingTotalsForBucket,
  type U3125ArAgingCustomerDetailReport,
  type U3125ArAgingSummaryCustomerRow,
} from "../../U3125Service";
import U3125ArAgingCustomerExpandedTable from "./U3125ArAgingCustomerExpandedTable";
import U3125ArAgingInlineSpinner from "./U3125ArAgingInlineSpinner";
import type { U2000AgingBucket } from "../../../u2000Core/constants/U2000AgingBuckets";

export interface U3125ArAgingHierarchySectionProps {
  customers: U3125ArAgingSummaryCustomerRow[];
  expandedCustomerId: string | null;
  loadingCustomerId: string | null;
  customerDetail: U3125ArAgingCustomerDetailReport | null;
  selectedProjectId: string | null;
  onToggleCustomer: (customerId: string) => void;
  onSelectProject: (projectId: string | null) => void;
  selectedBucket?: U2000AgingBucket | null;
}

const U3125ArAgingHierarchySection: React.FC<U3125ArAgingHierarchySectionProps> = ({
  customers,
  expandedCustomerId,
  loadingCustomerId,
  customerDetail,
  selectedProjectId,
  onToggleCustomer,
  onSelectProject,
  selectedBucket = null,
}) => {
  const { t } = useTranslation();

  if (!customers.length) {
    return (
      <p className="text-sm text-muted-foreground">{t("msgArAgingNoCustomers", { ns: NS })}</p>
    );
  }

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{t("lblArAgingCustomersSection", { ns: NS })}</h3>
      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
        {customers.map((row) => {
          const customerId = row.customer.id;
          const expanded = expandedCustomerId === customerId;
          const isLoadingDetail = loadingCustomerId === customerId;
          const customerLabel = u3125FormatArAgingCustomerLabel(row.customer);
          const detailForCustomer =
            expanded && customerDetail?.customer.id === customerId ? customerDetail : null;
          return (
            <li key={customerId} className="bg-white">
              <button
                type="button"
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => onToggleCustomer(customerId)}
                aria-expanded={expanded}
              >
                <span className="mt-0.5 shrink-0 text-muted-foreground">
                  {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium">{customerLabel}</div>
                  {!expanded ? (
                    <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                      {(() => {
                        const totals = selectedBucket
                          ? u3125ArAgingTotalsForBucket(row.buckets, selectedBucket)
                          : row.totals;
                        return (
                          <>
                            {t("colArAgingInvoiceCount", { ns: NS })}: {totals.invoiceCount} —{" "}
                            {t("colArAgingBalanceDue", { ns: NS })}: {u3125FormatCurrency(totals.balanceDue)}
                          </>
                        );
                      })()}
                    </div>
                  ) : null}
                  {isLoadingDetail ? (
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>{t("msgArAgingLoadingCustomer", { ns: NS })}</span>
                      <U3125ArAgingInlineSpinner className="h-3 w-3" />
                    </div>
                  ) : null}
                </div>
              </button>
              {expanded && !isLoadingDetail && detailForCustomer ? (
                <div className="border-t border-gray-100 px-3 pb-3 pt-1">
                  {detailForCustomer.projects?.length ? (
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {t("lblArAgingFilterInvoicesByProject", { ns: NS })}:
                      </span>
                      {detailForCustomer.projects.map((pr) => {
                        const projectId = pr.project.id;
                        const isSelected = selectedProjectId === projectId;
                        const projectLabel = u3125FormatArAgingProjectLabel(pr.project);
                        return (
                          <button
                            key={projectId}
                            type="button"
                            className={`rounded-full border px-2 py-0.5 text-xs font-medium transition-colors ${
                              isSelected
                                ? "border-primary-300 bg-primary-50 text-primary-800 ring-1 ring-primary-200"
                                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                            }`}
                            onClick={() => onSelectProject(isSelected ? null : projectId)}
                            title={t("lblArAgingFilterInvoicesByProject", { ns: NS })}
                          >
                            {projectLabel}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                  <U3125ArAgingCustomerExpandedTable
                    customerDetail={detailForCustomer}
                    customerLabel={customerLabel}
                    selectedBucket={selectedBucket}
                    projectFilterId={selectedProjectId}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default U3125ArAgingHierarchySection;
